import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { VoiceManager, PeerChangeInfo } from '../lib/voice-manager';

@customElement('jgz-voice-controls')
export class JgzVoiceControls extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ attribute: false }) voiceManager: VoiceManager | null = null;
  @state() private enabledPeerIds = new Set<string>();
  @state() private localEnabled = false;
  @state() private localMuted = true;

  private peerUnsub: (() => void) | null = null;
  private stateUnsub: (() => void) | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this.syncFromManager();
    if (this.voiceManager) {
      this.subscribePeer();
      this.subscribeState();
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.peerUnsub?.();
    this.peerUnsub = null;
    this.stateUnsub?.();
    this.stateUnsub = null;
  }

  willUpdate(changed: Map<string, unknown>): void {
    if (changed.has('voiceManager')) {
      this.syncFromManager();
      if (this.voiceManager) {
        this.subscribePeer();
        this.subscribeState();
        if (this.voiceManager.consumeAutoStartOnMount()) {
          this.voiceManager.start().catch(() => {});
        }
      } else {
        this.peerUnsub?.();
        this.peerUnsub = null;
        this.stateUnsub?.();
        this.stateUnsub = null;
      }
    }
  }

  private syncFromManager(): void {
    if (!this.voiceManager) {
      this.localEnabled = false;
      this.localMuted = true;
      return;
    }
    this.localEnabled = this.voiceManager.isEnabled();
    this.localMuted = !this.voiceManager.isMicActive();
  }

  private subscribePeer(): void {
    if (!this.voiceManager) return;
    this.peerUnsub = this.voiceManager.onPeerChange((info) => this.onPeer(info));
  }

  private subscribeState(): void {
    if (!this.voiceManager) return;
    this.stateUnsub = this.voiceManager.onStateChange(() => this.syncFromManager());
  }

  private onPeer(info: PeerChangeInfo): void {
    if (info.enabled) this.enabledPeerIds.add(info.playerId);
    else this.enabledPeerIds.delete(info.playerId);
    this.requestUpdate();
    this.dispatchEvent(
      new CustomEvent('jgz-voice-peer-count', {
        detail: { count: this.enabledPeerIds.size },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private onClick = (e: Event): void => {
    e.stopPropagation();
    if (!this.voiceManager) return;
    if (!this.localEnabled) {
      const p = this.voiceManager.start();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {});
      }
    } else if (!this.localMuted) {
      this.voiceManager.setMuted(true);
    } else {
      this.voiceManager.setMuted(false);
    }
  };

  render() {
    if (!this.voiceManager) return nothing;
    const micActive = this.localEnabled && !this.localMuted;
    const state = !this.localEnabled ? 'off' : micActive ? 'on' : 'muted';
    const label = !this.localEnabled
      ? 'Activar micrófono'
      : micActive
      ? 'Silenciar micrófono'
      : 'Reactivar micrófono';
    const showSlash = state === 'muted';
    return html`
      <button
        type="button"
        class="voice-mic-icon voice-mic-${state}"
        @click=${this.onClick}
        aria-label=${label}
        aria-pressed=${micActive ? 'true' : 'false'}
        data-testid="voice-toggle"
        data-state=${state}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"></path>
          <path d="M19 10v1a7 7 0 0 1-14 0v-1"></path>
          <line x1="12" y1="19" x2="12" y2="22"></line>
          ${showSlash ? html`<line x1="2" y1="2" x2="22" y2="22"></line>` : nothing}
        </svg>
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'jgz-voice-controls': JgzVoiceControls;
  }
}